const VALID_VERSIONS = new Set(["v1", "v2"]);
const VALID_EVENT_TYPES = new Set([
  "fallback",
  "error",
  "render",
  "version-switch",
  "abandon",
]);
const VALID_SWITCH_OUTCOMES = new Set(["navigated", "blocked", "failed"]);
const EVENT_CODE_PATTERN = /^[a-z0-9][a-z0-9._-]{0,119}$/i;
const FALLBACK_REASONS = new Set([
  "cohort-disabled",
  "destination-disabled",
  "global-kill-switch",
  "invalid-readiness",
  "pilot-policy",
  "policy",
  "pre-render-before-render",
  "pre-render-load",
  "v2-context-incompatible",
  "v2-missing",
  "v2-not-approved",
]);
const ERROR_REASONS_BY_PHASE = new Map([
  ["before-render", new Set(["gateway"])],
  ["load", new Set(["gateway"])],
  ["navigation", new Set(["router"])],
  ["navigation-read", new Set(["projection"])],
  ["recovery", new Set(["router"])],
]);
const ERROR_PHASES = new Set(ERROR_REASONS_BY_PHASE.keys());
const RENDER_PHASES = new Set(["feature-stable", "shell"]);
const VERSION_SWITCH_REASONS = new Set([
  "cohort-disabled",
  "context-loss",
  "destination-disabled",
  "development-preview",
  "dirty",
  "global-kill-switch",
  "invalid-readiness",
  "navigation-pending",
  "operation-pending",
  "pilot-policy",
  "policy",
  "preference-save-failed",
  "preferred",
  "router",
  "v2-context-incompatible",
  "v2-missing",
  "v2-not-approved",
]);
const ABANDON_PHASES = new Set([
  // Kept as an explicit contract code for callers measuring post-render exit.
  "after-render",
  "navigation-read",
  "navigation-superseded-before-feature-stable",
  "pagehide-before-feature-stable",
  "visibility-hidden-before-feature-stable",
]);

const isNonEmptyString = (value) => (
  typeof value === "string" && value.trim() !== ""
);

const requireString = (event, key) => {
  if (!isNonEmptyString(event[key])) {
    throw new TypeError(`${key} must be a non-empty string`);
  }
  return event[key].trim();
};

const requireCode = (event, key) => {
  const value = requireString(event, key);
  if (!EVENT_CODE_PATTERN.test(value)) {
    throw new TypeError(`${key} must be a structured code`);
  }
  return value;
};

const requireAllowedCode = (event, key, allowed) => {
  const value = requireCode(event, key);
  if (!allowed.has(value)) {
    throw new TypeError(`${key} is not supported`);
  }
  return value;
};

const optionalAllowedCode = (event, key, allowed) => (
  isNonEmptyString(event[key])
    ? requireAllowedCode(event, key, allowed)
    : undefined
);

const requireVersion = (event, key = "version") => {
  if (!VALID_VERSIONS.has(event[key])) {
    throw new TypeError(`${key} must be v1 or v2`);
  }
  return event[key];
};

const requireDuration = (event) => {
  if (!Number.isFinite(event.durationMs) || event.durationMs < 0) {
    throw new TypeError("durationMs must be a positive finite number or zero");
  }
  return Math.round(event.durationMs);
};

const requireTimestamp = (value) => {
  if (!Number.isFinite(value) || value < 0) {
    throw new TypeError("occurredAt must be a positive finite timestamp or zero");
  }
  return Math.round(value);
};

/**
 * Builds the only event envelope accepted by the pilot telemetry seam.
 *
 * Every returned object is reconstructed from an explicit allowlist. Caller
 * fields such as URLs, identities, search terms or business context are never
 * copied, even when this function is used defensively by a transport adapter.
 * `destinationId` deliberately remains a structural code here: membership in
 * the navigation registry belongs to the registry/pilot-control boundary, not
 * this privacy envelope leaf module.
 */
export const createPilotEvent = (event, occurredAt) => {
  if (!event || !VALID_EVENT_TYPES.has(event.type)) {
    throw new TypeError("event.type is not supported");
  }

  const common = {
    schemaVersion: 1,
    type: event.type,
    destinationId: requireCode(event, "destinationId"),
    occurredAt: requireTimestamp(occurredAt),
  };

  if (event.type === "fallback") {
    return Object.freeze({
      ...common,
      fromVersion: "v2",
      toVersion: "v1",
      reason: requireAllowedCode(event, "reason", FALLBACK_REASONS),
    });
  }
  if (event.type === "error") {
    const phase = requireAllowedCode(
      event,
      "phase",
      ERROR_PHASES,
    );
    return Object.freeze({
      ...common,
      version: requireVersion(event),
      phase,
      reason: requireAllowedCode(
        event,
        "reason",
        ERROR_REASONS_BY_PHASE.get(phase),
      ),
    });
  }
  if (event.type === "render") {
    return Object.freeze({
      ...common,
      version: requireVersion(event),
      durationMs: requireDuration(event),
      phase: requireAllowedCode(event, "phase", RENDER_PHASES),
    });
  }
  if (event.type === "version-switch") {
    const outcome = requireCode(event, "outcome");
    if (!VALID_SWITCH_OUTCOMES.has(outcome)) {
      throw new TypeError("outcome is not supported");
    }
    const fromVersion = requireVersion(event, "fromVersion");
    const toVersion = requireVersion(event, "toVersion");
    if (fromVersion === toVersion) {
      throw new TypeError("version-switch must change version");
    }
    const normalized = {
      ...common,
      fromVersion,
      toVersion,
      outcome,
    };
    const reason = optionalAllowedCode(event, "reason", VERSION_SWITCH_REASONS);
    return Object.freeze(reason ? { ...normalized, reason } : normalized);
  }

  return Object.freeze({
    ...common,
    version: requireVersion(event),
    durationMs: requireDuration(event),
    phase: requireAllowedCode(event, "phase", ABANDON_PHASES),
  });
};

export const sanitizePublishedPilotEvent = (event) => (
  createPilotEvent(event, event?.occurredAt)
);
